// Flow 3 - Feedback overview (docs/phase-0/slice6-ux-flows.md §5; slice6-component-designs.md §5).
// The `/apps/:appId/feedback` AppDetail sub-nav tab - reads `useOutletContext<AppDetailContext>()`
// exactly as PulseTab does (UX §2.1). Renders the themes (title, count, sentiment via icon+text,
// status, the D45 escalation), the top requests, and the closed-loop link. Founder-supplied
// feedback is themed alongside beacon feedback and tagged by source at the API; this read view
// surfaces the resulting themes. The add-feedback form + the vote/import actions reuse existing
// components and are a follow-up; the overview read is the load-bearing AC (AC-2).
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { FeedbackOverview, FeedbackThemeStatus } from "@nightcrew/shared";
import type { AppDetailContext } from "../AppDetail.js";
import { FeedbackSentimentIndicator } from "../../components/FeedbackSentimentIndicator.js";
import { getFeedbackOverview } from "../../api/feedback.js";
import { feedbackCopy } from "../../copy/feedback.js";

type FeedbackLoad =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly overview: FeedbackOverview };

/** Status -> a customer-safe chip label. Only the copy-backed statuses are keyed; the rest fall back
 * to a humanized enum so a new status never crashes the render. */
const statusLabel = (status: FeedbackThemeStatus): string => {
  if (status === "fixed") return feedbackCopy("feedback.themes.status.fixed");
  if (status === "watching") return feedbackCopy("feedback.themes.status.watching");
  if (status === "praise") return feedbackCopy("feedback.themes.status.praise");
  return status.replace(/_/g, " ");
};

export const FeedbackTab = (): React.JSX.Element => {
  const { app } = useOutletContext<AppDetailContext>();
  const [load, setLoad] = useState<FeedbackLoad>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const cancelled = { current: false };
    setLoad({ status: "loading" });
    void (async () => {
      const result = await getFeedbackOverview(app.id);
      if (cancelled.current) return;
      setLoad(isErr(result) ? { status: "error" } : { status: "ready", overview: result.value });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [app.id, attempt]);

  if (load.status === "loading") {
    return (
      <div className="wrap">
        <h1 className="h2">{app.name}</h1>
        <p className="muted mt8" role="status" style={{ fontSize: 12.5 }}>
          {feedbackCopy("feedback.loading.status")}
        </p>
        <div className="card pad skeleton-card mt16" />
      </div>
    );
  }

  if (load.status === "error") {
    return (
      <div className="wrap">
        <h1 className="h2">{app.name}</h1>
        <div className="card state-card mt16">
          <p>{feedbackCopy("feedback.error.title")}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setAttempt((a) => a + 1);
            }}
          >
            {feedbackCopy("feedback.error.retryCta")}
          </button>
        </div>
      </div>
    );
  }

  const { overview } = load;
  const escalatedThemeIds = new Set(overview.escalations.filter((e) => e.escalated).map((e) => e.themeId));

  return (
    <div className="wrap">
      <h1 className="h2">{app.name}</h1>
      <span className="sr-only" aria-live="polite">
        {feedbackCopy("feedback.loaded.status")}
      </span>

      {escalatedThemeIds.size > 0 ? (
        <div className="card pad mt16 theme-escalation-banner" role="status" data-testid="feedback-escalation-banner">
          {feedbackCopy("feedback.escalation.banner")}
        </div>
      ) : null}

      {/* Themes */}
      <h2 className="h3 mt16">{feedbackCopy("feedback.themes.heading")}</h2>
      {overview.themes.length === 0 ? (
        <p className="muted mt8" data-testid="feedback-themes-empty">
          {feedbackCopy("feedback.themes.empty")}
        </p>
      ) : (
        <div className="stack gap12 mt8">
          {overview.themes.map((theme) => (
            <div key={theme.id} className="card pad" data-testid="feedback-theme-card">
              <div className="control-row">
                <strong>{theme.title}</strong>
                <span className="muted">{theme.count}</span>
              </div>
              <div className="mt8" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <FeedbackSentimentIndicator sentiment={theme.sentiment} />
                <span className="status-chip neutral">{statusLabel(theme.status)}</span>
                {escalatedThemeIds.has(theme.id) ? (
                  <span className="status-chip risk">{feedbackCopy("feedback.themes.escalatedChip")}</span>
                ) : null}
                {theme.linkedFixId !== null ? (
                  <span className="muted" style={{ fontSize: 12.5 }}>
                    {feedbackCopy("feedback.themes.linkedFix")}
                  </span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Top requests */}
      <h2 className="h3 mt24">{feedbackCopy("feedback.topRequests.heading")}</h2>
      {overview.topRequests.length === 0 ? (
        <p className="muted mt8" data-testid="feedback-requests-empty">
          {feedbackCopy("feedback.topRequests.empty")}
        </p>
      ) : (
        <div className="stack gap12 mt8">
          {overview.topRequests.map((req) => (
            <div key={req.id} className="control-row card pad" data-testid="feedback-request-row">
              <span>{req.title}</span>
              <span className="muted">{feedbackCopy(`feedback.topRequests.size.${req.size}`)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
